/* ============================================================================
 * KIKI / WagNudge attribution
 *
 * Captures first-touch marketing attribution and dispatches WagNudge-owned
 * events. It does not load pixels, SDKs, or remote endpoints.
 * ============================================================================
 */

(function () {
  'use strict';

  if (window.__kikiAttributionLoaded) return;
  window.__kikiAttributionLoaded = true;

  var STORAGE_KEY = 'kiki.attribution.v1';
  var SOCIAL_ARRIVAL_STORAGE_KEY = 'kiki.social_arrival.v1';
  var SOCIAL_ARRIVAL_EVENT = 'wagn:canonical_arrival';
  var SOCIAL_ARRIVAL_MAX_AGE_MS = 30 * 60 * 1000;
  var UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
  var CLICK_ID_KEYS = ['gclid', 'fbclid', 'ttclid', 'msclkid', 'sclid', 'epik', 'yclid', 'twclid'];
  var MARKETING_DATA_KEYS = UTM_KEYS.concat(CLICK_ID_KEYS, [
    'referrer',
    'landing_path',
    'landing_url',
    'landing_url_clean',
    'captured_at',
    'updated_at'
  ]);
  var MARKETING_EVENT_KEYS = MARKETING_DATA_KEYS.concat([
    'channel',
    'channel_preset',
    'campaign_id',
    'campaign_handle',
    'social_campaign_id',
    'social_angle_id'
  ]);
  /* WagNudge-owned context fields that can be added to applicable events
     (locale, language, country/market, currency). These are read from the
     page or from the nearest kiki-component wrapper; they are NOT
     marketing attribution and are therefore NOT scrubbed when only
     marketing consent is missing. product_handle and offer_id are
     machine identifiers that travel with product-bridge events. */
  var KIKI_CONTEXT_KEYS = [
    'content_id',
    'platform',
    'problem_id',
    'landing_variant',
    'locale',
    'language',
    'country',
    'market_id',
    'currency',
    'breed_id',
    'product_handle',
    'offer_id'
  ];
  var REQUIRED_EVENT_METADATA = [
    'content_id',
    'platform',
    'problem_id',
    'landing_variant'
  ];
  var SHOPIFY_CUSTOM_EVENT_MAP = {
    'wagn:landing_view': 'wagnudge:landing_view',
    'wagn:primary_cta_click': 'wagnudge:primary_cta_click',
    'wagn:secondary_cta_click': 'wagnudge:secondary_cta_click',
    'wagn:route_selection': 'wagnudge:route_selection',
    'wagn:source_initialized': 'wagnudge:source_initialized',
    'wagn:breed_selector_view': 'wagnudge:breed_selector_view',
    'wagn:breed_selected': 'wagnudge:breed_selected',
    'wagn:breed_problem_selected': 'wagnudge:breed_problem_selected',
    'wagn:mixed_breed_selected': 'wagnudge:mixed_breed_selected',
    'wagn:breed_size_filter': 'wagnudge:breed_size_filter'
  };
  var BREED_SELECTOR_DOM_EVENTS = Object.keys(SHOPIFY_CUSTOM_EVENT_MAP).filter(function (name) {
    return name.indexOf('wagn:breed_') === 0 ||
      name === 'wagn:mixed_breed_selected' ||
      name === 'wagn:source_initialized';
  });
  var CUSTOMER_EVENT_FIELDS = [
    'campaign_id',
    'component',
    'breed_key',
    'breed_size',
    'route_key',
    'destination_path',
    'source_key',
    'medium_key',
    'account_key',
    'placement_key',
    'content_key',
    'campaign_key',
    'landing_key',
    'entry_key'
  ];
  var CUSTOMER_EVENT_COMPONENTS = {
    'wagn:landing_view': 'social_landing',
    'wagn:primary_cta_click': 'primary_cta',
    'wagn:secondary_cta_click': 'secondary_cta',
    'wagn:route_selection': 'route_selection',
    'wagn:source_initialized': 'source_attribution',
    'wagn:breed_selector_view': 'breed_selector',
    'wagn:breed_selected': 'breed_grid',
    'wagn:breed_problem_selected': 'problem_routes',
    'wagn:mixed_breed_selected': 'breed_grid',
    'wagn:breed_size_filter': 'size_filter'
  };

  function privacyApi() {
    return window.Shopify && window.Shopify.customerPrivacy
      ? window.Shopify.customerPrivacy
      : null;
  }

  function isYes(value) {
    return value === 'yes';
  }

  function readVisitorConsent(cp) {
    if (!cp || typeof cp.currentVisitorConsent !== 'function') return {};
    try {
      return cp.currentVisitorConsent() || {};
    } catch (err) {
      return {};
    }
  }

  function processingAllowed(cp, methodName, fallbackValue) {
    if (!cp) return false;
    if (typeof cp[methodName] === 'function') {
      try {
        return cp[methodName]() === true;
      } catch (err) {
        return false;
      }
    }
    return isYes(fallbackValue);
  }

  function readConsentCategories() {
    var cp = privacyApi();
    var visitor = readVisitorConsent(cp);
    return {
      available: !!cp,
      analytics: processingAllowed(cp, 'analyticsProcessingAllowed', visitor.analytics),
      marketing: processingAllowed(cp, 'marketingAllowed', visitor.marketing),
      preferences: processingAllowed(cp, 'preferencesProcessingAllowed', visitor.preferences),
      sale_of_data: processingAllowed(cp, 'saleOfDataAllowed', visitor.sale_of_data)
    };
  }

  function analyticsProcessingAllowedNow() {
    return readConsentCategories().analytics;
  }

  function marketingAllowedNow() {
    return readConsentCategories().marketing;
  }

  function consentBasis(consent) {
    if (consent.analytics) return 'granted';
    return consent.available ? 'pending_or_denied' : 'api_missing';
  }

  function stripQueryString(href) {
    try {
      var url = new URL(href, window.location.origin);
      url.search = '';
      url.hash = '';
      return url.toString();
    } catch (err) {
      return window.location.pathname;
    }
  }

  function readUrlParams() {
    var out = {};
    try {
      var search = new URL(window.location.href).searchParams;
      UTM_KEYS.concat(CLICK_ID_KEYS).forEach(function (key) {
        var value = search.get(key);
        if (value) out[key] = value;
      });
    } catch (err) {
      /* URL parsing failed; leave attribution empty. */
    }
    return out;
  }

  function readStored() {
    try {
      var raw = window.sessionStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch (err) {
      return {};
    }
  }

  function writeStored(payload) {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (err) {
      /* sessionStorage may be unavailable. */
    }
  }

  function clearStored() {
    try {
      window.sessionStorage.removeItem(STORAGE_KEY);
    } catch (err) {
      /* sessionStorage may be unavailable. */
    }
  }

  function removeMarketingData(payload) {
    var clean = Object.assign({}, payload || {});
    MARKETING_DATA_KEYS.forEach(function (key) {
      delete clean[key];
    });
    return clean;
  }

  function captureMarketingAttribution() {
    if (!marketingAllowedNow()) return {};

    var stored = readStored();
    var fromUrl = readUrlParams();
    var now = new Date().toISOString();
    var next = Object.assign({}, fromUrl, stored);

    if (!next.captured_at) next.captured_at = now;
    next.updated_at = now;
    if (!next.referrer) next.referrer = document.referrer || '';
    if (!next.landing_path) next.landing_path = window.location.pathname;
    if (!next.landing_url) next.landing_url = window.location.href;
    if (!next.landing_url_clean) next.landing_url_clean = stripQueryString(window.location.href);

    writeStored(next);
    return next;
  }

  function publishConsentState() {
    var consent = readConsentCategories();
    var prior = window.KIKI_ATTRIBUTION && typeof window.KIKI_ATTRIBUTION === 'object'
      ? window.KIKI_ATTRIBUTION
      : {};

    if (!consent.marketing) {
      prior = removeMarketingData(prior);
      if (consent.available) clearStored();
    }

    window.KIKI_ATTRIBUTION = Object.assign({}, prior, {
      consent_basis: consentBasis(consent),
      consent_pending: !consent.analytics,
      consent_analytics: consent.analytics,
      consent_marketing: consent.marketing,
      consent_preferences: consent.preferences,
      consent_sale_of_data: consent.sale_of_data
    });

    return consent;
  }

  function refreshAttribution() {
    var consent = publishConsentState();
    if (consent.marketing) {
      window.KIKI_ATTRIBUTION = Object.assign(
        {},
        window.KIKI_ATTRIBUTION,
        captureMarketingAttribution()
      );
    }
    return consent;
  }

  function buildBasePayload(consent) {
    var base = {
      path: window.location.pathname,
      url: stripQueryString(window.location.href),
      title: document.title,
      template: (document.querySelector('main[data-template]') || {}).dataset &&
        document.querySelector('main[data-template]').dataset.template || '',
      timestamp: new Date().toISOString(),
      consent_basis: consentBasis(consent),
      consent_pending: !consent.analytics,
      consent_analytics: consent.analytics,
      consent_marketing: consent.marketing,
      consent_preferences: consent.preferences,
      consent_sale_of_data: consent.sale_of_data
    };

    if (consent.marketing) {
      Object.assign(base, captureMarketingAttribution());
    }

    var ctx = readKikiContext();
    KIKI_CONTEXT_KEYS.forEach(function (key) {
      if (ctx[key] && !base[key]) base[key] = ctx[key];
    });

    return base;
  }

  function readKikiContext() {
    var ctx = {};
    try {
      var html = document.documentElement;
      if (html) {
        ctx.locale = html.getAttribute('lang') || '';
      }
    } catch (err) {
      /* ignore — lang attribute is informational */
    }
    try {
      var root = document.querySelector('[data-kiki-component]');
      if (root) {
        if (!ctx.breed_id) ctx.breed_id = root.getAttribute('data-kiki-breed-id') || '';
        if (!ctx.product_handle) ctx.product_handle = root.getAttribute('data-kiki-product-handle') || '';
        if (!ctx.offer_id) ctx.offer_id = root.getAttribute('data-kiki-offer-id') || '';
        if (!ctx.content_id) ctx.content_id = root.getAttribute('data-kiki-content-id') || '';
        if (!ctx.platform) ctx.platform = root.getAttribute('data-kiki-platform') || '';
        if (!ctx.problem_id) ctx.problem_id = root.getAttribute('data-kiki-problem-id') || '';
        if (!ctx.landing_variant) ctx.landing_variant = root.getAttribute('data-kiki-landing-variant') || '';
      }
    } catch (err) {
      /* ignore — context is optional */
    }
    try {
      KIKI_CONTEXT_KEYS.forEach(function (key) {
        var meta = document.querySelector('meta[name="kiki:' + key + '"]');
        var value = meta && (meta.content || meta.getAttribute('content'));
        if (value && !ctx[key]) ctx[key] = value;
      });
    } catch (err) {
      /* ignore — meta tags are optional */
    }
    return ctx;
  }

  function hasRequiredEventMetadata(payload) {
    return REQUIRED_EVENT_METADATA.every(function (key) {
      return typeof payload[key] === 'string' && payload[key].trim() !== '';
    });
  }

  function readNonEmptyString(value) {
    return typeof value === 'string' && value.trim() !== '' ? value.trim() : '';
  }

  function readInternalDestinationPath(value) {
    var candidate = readNonEmptyString(value);
    if (!candidate) return '';
    try {
      var parsed = new URL(candidate, window.location.origin);
      if (parsed.origin !== window.location.origin || !parsed.pathname) return '';
      return parsed.pathname;
    } catch (err) {
      return '';
    }
  }

  function buildShopifyCustomerEventPayload(domEventName, source) {
    source = source || {};
    var payload = {};

    if (!SHOPIFY_CUSTOM_EVENT_MAP[domEventName]) return null;
    CUSTOMER_EVENT_FIELDS.forEach(function (key) {
      var value = key === 'destination_path'
        ? readInternalDestinationPath(source[key])
        : readNonEmptyString(source[key]);
      if (value) payload[key] = value;
    });
    if (!payload.component) {
      payload.component = CUSTOMER_EVENT_COMPONENTS[domEventName] || '';
    }

    return payload;
  }

  function publishShopifyCustomerEvent(domEventName, safePayload) {
    var eventName = SHOPIFY_CUSTOM_EVENT_MAP[domEventName];
    if (!eventName || !analyticsProcessingAllowedNow()) return false;

    var payload = buildShopifyCustomerEventPayload(domEventName, safePayload);
    if (!payload) return false;

    var analytics = window.Shopify && window.Shopify.analytics;
    if (!analytics || typeof analytics.publish !== 'function') return false;

    try {
      var result = analytics.publish(eventName, payload);
      if (result && typeof result.catch === 'function') {
        result.catch(function () {
          /* Customer Events failures never block the storefront or retry. */
        });
      }
      return result !== false;
    } catch (err) {
      return false;
    }
  }

  function sanitizeEventDetail(detail, marketingAllowed) {
    var clean = Object.assign({}, detail || {});
    if (marketingAllowed) return clean;

    MARKETING_EVENT_KEYS.forEach(function (key) {
      delete clean[key];
    });
    Object.keys(clean).forEach(function (key) {
      if (/_href$/.test(key) && clean[key]) {
        clean[key] = stripQueryString(clean[key]);
      }
    });
    return clean;
  }

  function dispatchWagn(name, detail, options) {
    var consent = readConsentCategories();

    /* Fail closed: page, campaign, view, and interaction data must not reach
       downstream listeners before Shopify permits analytics processing. */
    if (!consent.analytics || !analyticsProcessingAllowedNow()) return false;

    publishConsentState();
    var payload = Object.assign(
      buildBasePayload(consent),
      sanitizeEventDetail(detail, consent.marketing)
    );
    if (options && options.suppressMarketing) {
      payload = removeMarketingData(payload);
      MARKETING_EVENT_KEYS.forEach(function (key) {
        delete payload[key];
      });
    }
    var pageContext = readKikiContext();
    REQUIRED_EVENT_METADATA.forEach(function (key) {
      if ((!payload[key] || String(payload[key]).trim() === '') && pageContext[key]) {
        payload[key] = pageContext[key];
      }
    });
    if (!hasRequiredEventMetadata(payload)) return false;

    try {
      if (!(options && options.domAlreadyDispatched)) {
        document.dispatchEvent(new CustomEvent(name, {
          detail: payload,
          bubbles: true
        }));
      }
      publishShopifyCustomerEvent(name, {
        campaign_id: readNonEmptyString(detail && detail.campaign_id) ||
          readNonEmptyString(detail && detail.social_campaign_id),
        component: detail && detail.component,
        breed_key: detail && detail.breed_key,
        breed_size: detail && detail.breed_size,
        route_key: detail && detail.route_key,
        destination_path: readInternalDestinationPath(
          detail && (detail.destination_path || detail.cta_href)
        ),
        source_key: detail && detail.source_key,
        medium_key: detail && detail.medium_key,
        account_key: detail && detail.account_key,
        placement_key: detail && detail.placement_key,
        content_key: detail && detail.content_key,
        campaign_key: detail && detail.campaign_key,
        landing_key: detail && detail.landing_key,
        entry_key: detail && detail.entry_key
      });
      return true;
    } catch (err) {
      return false;
    }
  }

  /* Social Landing events describe campaign interactions, but their event
     payloads must never duplicate raw session attribution. Attribution may
     remain in first-party session storage when Marketing consent permits it;
     every event emitted from a Social Landing uses this single privacy-safe
     dispatcher so the two concerns cannot drift apart. */
  function dispatchSocialLandingEvent(name, detail) {
    return dispatchWagn(name, detail, { suppressMarketing: true });
  }

  function bridgeBreedSelectorEvent(event) {
    if (!event || event.target !== window || BREED_SELECTOR_DOM_EVENTS.indexOf(event.type) === -1) {
      return;
    }
    var detail = Object.assign({}, event.detail || {});
    var selectorRoot = document.querySelector('[data-kiki-breed-selector]');
    detail.content_id = readNonEmptyString(detail.content_id) || 'social_tiktok_breed_selector_v1';
    detail.platform = readNonEmptyString(detail.platform) ||
      readNonEmptyString(detail.source_key) || 'web';
    detail.problem_id = readNonEmptyString(detail.problem_id) || 'start_here';
    detail.landing_variant = 'breed_selector';
    detail.campaign_id = readNonEmptyString(detail.campaign_id) ||
      readNonEmptyString(selectorRoot && selectorRoot.getAttribute('data-campaign-id')) ||
      'tiktok_dog_breed_selector_v1';
    dispatchWagn(event.type, detail, {
      suppressMarketing: true,
      domAlreadyDispatched: true
    });
  }

  if (typeof window.addEventListener === 'function') {
    BREED_SELECTOR_DOM_EVENTS.forEach(function (eventName) {
      window.addEventListener(eventName, bridgeBreedSelectorEvent, false);
    });
  }

  var viewTrackingStarted = false;
  var socialLandingViewSent = false;
  var canonicalArrivalSent = false;
  var breedHubViewSent = false;
  var breedLaunchViewSent = false;
  var BREED_EVENTS = [
    'breed_hub_view',
    'breed_select',
    'breed_problem_click',
    'breed_solution_click',
    'breed_product_category_click',
    'breed_to_problem_transition'
    ,'breed_problem_select'
    ,'breed_problem_continue'
    ,'breed_guide_click'
    ,'optional_tool_reveal'
    ,'optional_tool_click'
    ,'mixed_unsure_click'
    ,'change_dog_click'
  ];
  var BREED_LAUNCH_EVENTS = [
    'wagnudge_breed_hub_view',
    'wagnudge_breed_route_select',
    'wagnudge_breed_problem_click',
    'wagnudge_breed_guide_click',
    'wagnudge_breed_product_click'
  ];

  function buildBreedEventDetail(element) {
    var root = element && element.closest('[data-kiki-breed-id]');
    return {
      breed_id: (element && element.getAttribute('data-breed-id')) || (root && root.getAttribute('data-kiki-breed-id')) || '',
      problem_id: (element && element.getAttribute('data-problem-id')) || '',
      problem_key: (element && element.getAttribute('data-problem-key')) || '',
      article_handle: (element && element.getAttribute('data-article-handle')) || '',
      product_handle: (element && element.getAttribute('data-product-handle')) || '',
      route_key: (element && element.getAttribute('data-route-key')) || '',
      solution_id: (element && element.getAttribute('data-solution-id')) || '',
      product_category_id: (element && element.getAttribute('data-product-category-id')) || '',
      cta_label: element && (element.textContent || '').trim() || '',
      cta_href: element && element.getAttribute('href') || '',
      event_category: 'breed_first'
    };
  }

  function dispatchBreedEvent(eventName, element) {
    if (BREED_EVENTS.indexOf(eventName) === -1) return false;
    var detail = buildBreedEventDetail(element);
    if (!detail.breed_id) return false;
    return dispatchWagn('wagn:' + eventName, detail);
  }

  function buildBreedLaunchEventDetail(element, fields) {
    var root = element && element.closest('[data-kiki-breed-id]');
    var detail = {
      page_type: 'breed_context_hub',
      breed_handle: (root && root.getAttribute('data-kiki-breed-id')) || '',
      route_key: (element && element.getAttribute('data-problem-key')) || '',
      destination_type: (element && element.getAttribute('data-breed-destination-type')) || '',
      destination_handle: (element && element.getAttribute('data-breed-destination-handle')) || '',
      product_handle: (element && element.getAttribute('data-product-handle')) || '',
      interaction_method: '',
      event_version: 1
    };
    Object.assign(detail, fields || {});
    Object.keys(detail).forEach(function (key) {
      if (detail[key] === '') delete detail[key];
    });
    return detail;
  }

  function dispatchBreedLaunchEvent(eventName, element, fields) {
    if (BREED_LAUNCH_EVENTS.indexOf(eventName) === -1) return false;
    var detail = buildBreedLaunchEventDetail(element, fields);
    if (!detail.breed_handle) return false;
    var dispatched = dispatchWagn(eventName, detail, { suppressMarketing: true });
    if (dispatched) {
      if (!Array.isArray(window.WAGNUDGE_BREED_EVENT_QUEUE)) {
        window.WAGNUDGE_BREED_EVENT_QUEUE = [];
      }
      window.WAGNUDGE_BREED_EVENT_QUEUE.push({
        event_name: eventName,
        detail: Object.assign({}, detail)
      });
    }
    return dispatched;
  }

  function trackBreedHubView() {
    if (!analyticsProcessingAllowedNow()) return;
    var root = document.querySelector('[data-kiki-event="breed_hub_view"][data-kiki-breed-id]');
    if (!root) return;
    if (root.getAttribute('data-kiki-component') === 'breed-context-hub') {
      if (breedLaunchViewSent) return;
      breedLaunchViewSent = dispatchBreedLaunchEvent('wagnudge_breed_hub_view', root, {
        interaction_method: 'page_load'
      });
      return;
    }
    if (breedHubViewSent) return;
    breedHubViewSent = dispatchBreedEvent('breed_hub_view', root);
  }

  function onBreedEventClick(event) {
    if (!analyticsProcessingAllowedNow()) return;
    var target = event.target.closest('[data-kiki-event]');
    if (!target) return;
    var eventName = target.getAttribute('data-kiki-event') || '';
    if (eventName === 'breed_hub_view') return;
    dispatchBreedEvent(eventName, target);
  }

  document.addEventListener('kiki:breed-event', function (event) {
    var detail = event.detail || {};
    if (!analyticsProcessingAllowedNow() || !detail.element) return;
    dispatchBreedEvent(detail.eventName || '', detail.element);
  });

  document.addEventListener('kiki:breed-launch-event', function (event) {
    var detail = event.detail || {};
    if (!analyticsProcessingAllowedNow() || !detail.element) return;
    dispatchBreedLaunchEvent(detail.eventName || '', detail.element, detail.fields || {});
  });

  /* Product bridge viewport impression tracking.
     --------------------------------------------------------------
     wagn:product_bridge_view is a viewport impression, NOT an
     initialization event. Per-bridge IntersectionObservers defer
     dispatch until a bridge first becomes visible AND analytics
     processing is allowed. Each bridge is independently eligible
     for exactly one successful dispatch during the page lifetime.

     - productBridgeDispatched: WeakSet of elements that have already
       produced a successful impression. Acts as the once-only gate so
       repeated intersection callbacks, re-entry into view, DOM
       initialization re-runs, and visitorConsentCollected events
       can never produce a second event for the same bridge.
     - productBridgeEntries: Array of { root, observer, intersecting }
       entries so consent-change handlers can iterate bridges whose
       observers are still attached (without needing WeakMap keys).
     - Legacy fallback arrays are used when WeakSet/Array.findIndex
       are unavailable so the once-only contract still holds. */
  var productBridgeDispatched = (typeof WeakSet === 'function') ? new WeakSet() : null;
  var productBridgeDispatchedFallback = [];
  var productBridgeEntries = [];

  function productBridgeAlreadyDispatched(root) {
    if (productBridgeDispatched) return productBridgeDispatched.has(root);
    return productBridgeDispatchedFallback.indexOf(root) !== -1;
  }

  function productBridgeMarkDispatched(root) {
    if (productBridgeDispatched) {
      productBridgeDispatched.add(root);
      return;
    }
    if (productBridgeDispatchedFallback.indexOf(root) === -1) {
      productBridgeDispatchedFallback.push(root);
    }
  }

  function buildProductBridgeDetail(root) {
    return {
      product_mapped: root.getAttribute('data-kiki-product-mapped') === 'true',
      product_handle: root.getAttribute('data-kiki-product-handle') || '',
      offer_id: root.getAttribute('data-kiki-offer-id') || '',
      problem_slug: root.getAttribute('data-kiki-problem-slug') || '',
      problem_id: root.getAttribute('data-kiki-problem-id') || '',
      event_category: 'product_bridge'
    };
  }

  function tryDispatchProductBridge(root) {
    if (productBridgeAlreadyDispatched(root)) return false;
    if (!analyticsProcessingAllowedNow()) return false;
    var ok = dispatchWagn('wagn:product_bridge_view', buildProductBridgeDetail(root));
    if (ok) productBridgeMarkDispatched(root);
    return ok;
  }

  function unobserveProductBridgeEntry(entry) {
    if (entry && entry.observer && typeof entry.observer.unobserve === 'function') {
      try {
        entry.observer.unobserve(entry.root);
      } catch (err) {
        /* observer may already be disconnected; ignore */
      }
    }
    if (entry) entry.observer = null;
  }

  function findProductBridgeEntry(root) {
    for (var i = 0; i < productBridgeEntries.length; i++) {
      if (productBridgeEntries[i].root === root) return productBridgeEntries[i];
    }
    return null;
  }

  function isProductBridgeCurrentlyVisible(root) {
    /* Read the live bounding rect as a fallback for the latest known
       observer state. The observer's `intersecting` flag is preferred
       because it reflects the same threshold used for dispatch, but a
       rect check is enough to disambiguate a brand-new observer that
       has not yet fired its initial callback. */
    try {
      var rect = root.getBoundingClientRect();
      var vh = (window.innerHeight || document.documentElement.clientHeight || 0);
      var vw = (window.innerWidth || document.documentElement.clientWidth || 0);
      return rect.bottom > 0 && rect.top < vh && rect.right > 0 && rect.left < vw;
    } catch (err) {
      return false;
    }
  }

  function onProductBridgeView() {
    /* Attach one IntersectionObserver per product bridge. We do NOT
       dispatch at page initialization — wagn:product_bridge_view is a
       viewport impression. Dispatch is deferred until the bridge first
       becomes visible AND analytics processing is allowed. */
    var bridges = document.querySelectorAll('[data-kiki-component="product-bridge"]');
    Array.prototype.forEach.call(bridges, function (root) {
      /* Already dispatched — nothing to observe. */
      if (productBridgeAlreadyDispatched(root)) return;
      /* Already has an observer attached — bind() is idempotent. */
      if (findProductBridgeEntry(root)) return;

      var entry = { root: root, observer: null, intersecting: false };
      var observer = new IntersectionObserver(function (entries) {
        Array.prototype.forEach.call(entries, function (e) {
          entry.intersecting = !!e.isIntersecting;
          if (!e.isIntersecting) return;
          if (tryDispatchProductBridge(entry.root)) {
            unobserveProductBridgeEntry(entry);
          }
        });
      }, { threshold: 0.1 });
      entry.observer = observer;
      try {
        observer.observe(root);
      } catch (err) {
        /* IntersectionObserver can throw on detached roots; fall back
           to a rect-based dispatch on the next consent re-evaluation
           rather than silently dropping the bridge. */
        entry.observer = null;
      }
      productBridgeEntries.push(entry);
    });
  }

  function reevaluateVisibleProductBridges() {
    /* Consent just changed. For every observed-but-not-yet-dispatched
       bridge that is currently visible, attempt one dispatch. Bridges
       that are not visible remain observed and will dispatch on their
       next viewport entry. Bridges already dispatched are skipped. */
    if (!analyticsProcessingAllowedNow()) return;
    for (var i = 0; i < productBridgeEntries.length; i++) {
      var entry = productBridgeEntries[i];
      if (!entry || !entry.root) continue;
      if (productBridgeAlreadyDispatched(entry.root)) continue;
      var visible = entry.intersecting === true;
      if (!visible) {
        /* The observer may not have fired yet (e.g. consent was
           granted before the initial callback). Use the rect to
           decide whether the bridge is currently visible. */
        visible = isProductBridgeCurrentlyVisible(entry.root);
      }
      if (!visible) continue;
      if (tryDispatchProductBridge(entry.root)) {
        unobserveProductBridgeEntry(entry);
      }
    }
  }

  function trackViewProblemCards() {
    if (viewTrackingStarted || !analyticsProcessingAllowedNow()) return;
    viewTrackingStarted = true;

    var cards = document.querySelectorAll('[data-kiki-component="problem-selector"] [data-kiki-problem-slug]');
    var seen = {};

    Array.prototype.forEach.call(cards, function (el) {
      var slug = el.getAttribute('data-kiki-problem-slug');
      var id = el.getAttribute('data-kiki-problem-id') || slug;
      if (!slug || seen[slug]) return;
      seen[slug] = true;

      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          if (dispatchWagn('wagn:view_problem', {
            problem_slug: slug,
            problem_id: id,
            event_category: 'problem_discovery'
          })) {
            observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.4 });
      observer.observe(el);
    });
  }

  function trackSocialLandingView() {
    if (socialLandingViewSent || !analyticsProcessingAllowedNow()) return;

    var root = document.querySelector('[data-kiki-component="social-landing"]');
    if (!root) return;

    var eventName = root.getAttribute('data-kiki-landing-event') || 'wagn:social_landing_view';
    var detail = Object.assign({
      event_category: 'social_landing'
    }, readSocialLandingContext(root, root));

    socialLandingViewSent = dispatchSocialLandingEvent(eventName, detail);
    if (socialLandingViewSent && eventName !== 'wagn:social_landing_view') {
      dispatchSocialLandingEvent('wagn:social_landing_view', detail);
    }
  }

  function readSocialLandingContext(root, source) {
    source = source || root || null;
    if (!source) return {};

    var pick = function (sourceName, rootName) {
      var direct = source.getAttribute(sourceName);
      if (direct != null && direct !== '') return direct;
      return (root && root.getAttribute(rootName)) || '';
    };

    var productRoot = source.closest ? source.closest('[data-kiki-component="product-bridge"]') : null;
    var productHandle = pick('data-kiki-product-handle', 'data-kiki-product-handle');
    if (!productHandle && productRoot) {
      productHandle = productRoot.getAttribute('data-kiki-product-handle') || '';
    }
    var offerId = pick('data-kiki-offer-id', 'data-kiki-offer-id');
    if (!offerId && productRoot) {
      offerId = productRoot.getAttribute('data-kiki-offer-id') || '';
    }

    return {
      content_id: pick('data-kiki-cta-content-id', 'data-kiki-content-id'),
      platform: pick('data-kiki-cta-platform', 'data-kiki-platform'),
      problem_id: pick('data-kiki-cta-problem-id', 'data-kiki-problem-id'),
      landing_variant: pick('data-kiki-cta-landing-variant', 'data-kiki-landing-variant'),
      channel_preset: pick('data-kiki-cta-channel-preset', 'data-kiki-channel-preset'),
      channel: pick('data-kiki-cta-channel', 'data-kiki-channel'),
      campaign_id: pick('data-kiki-cta-campaign-id', 'data-kiki-campaign-id'),
      campaign_handle: pick('data-kiki-cta-campaign-handle', 'data-kiki-campaign-handle'),
      social_campaign_id: pick('data-kiki-cta-social-campaign-id', 'data-kiki-social-campaign-id'),
      social_angle_id: pick('data-kiki-cta-social-angle-id', 'data-kiki-social-angle-id'),
      destination_content_id: pick('data-kiki-cta-destination-content-id', 'data-kiki-destination-content-id'),
      route_key: pick('data-kiki-route-key', 'data-kiki-route-key'),
      problem_slug: pick('data-kiki-cta-problem-slug', 'data-kiki-problem-slug'),
      product_handle: productHandle,
      offer_id: offerId
    };
  }

  function writeSocialArrivalContext(detail) {
    if (!analyticsProcessingAllowedNow() || !hasRequiredEventMetadata(detail)) {
      return false;
    }
    if (!detail.destination_content_id || !detail.cta_href) return false;

    var destination;
    try {
      destination = new URL(detail.cta_href, window.location.origin);
    } catch (err) {
      return false;
    }
    if (destination.origin !== window.location.origin) return false;
    var targetSection = (destination.hash || '').replace(/^#/, '');
    if (!destination.pathname || !targetSection) return false;

    var context = {
      content_id: detail.content_id,
      platform: detail.platform,
      problem_id: detail.problem_id,
      landing_variant: detail.landing_variant,
      destination_content_id: detail.destination_content_id,
      destination_path: destination.pathname,
      target_section: targetSection,
      captured_at: new Date().toISOString()
    };

    if (marketingAllowedNow()) {
      var attribution = window.KIKI_ATTRIBUTION || {};
      UTM_KEYS.forEach(function (key) {
        if (attribution[key]) context[key] = attribution[key];
      });
    }

    try {
      window.sessionStorage.setItem(
        SOCIAL_ARRIVAL_STORAGE_KEY,
        JSON.stringify(context)
      );
      return true;
    } catch (err) {
      return false;
    }
  }

  function readSocialArrivalContext() {
    try {
      var raw = window.sessionStorage.getItem(SOCIAL_ARRIVAL_STORAGE_KEY);
      if (!raw) return null;
      var context = JSON.parse(raw);
      if (!context || !hasRequiredEventMetadata(context)) return null;
      if (context.landing_variant !== 'social_landing') return null;
      if (
        typeof context.destination_content_id !== 'string' ||
        context.destination_content_id.trim() === '' ||
        typeof context.destination_path !== 'string' ||
        context.destination_path.trim() === '' ||
        typeof context.target_section !== 'string' ||
        context.target_section.trim() === ''
      ) {
        return null;
      }
      var capturedAt = Date.parse(context.captured_at || '');
      if (
        !Number.isFinite(capturedAt) ||
        Date.now() - capturedAt > SOCIAL_ARRIVAL_MAX_AGE_MS
      ) {
        window.sessionStorage.removeItem(SOCIAL_ARRIVAL_STORAGE_KEY);
        return null;
      }
      return context;
    } catch (err) {
      return null;
    }
  }

  function clearSocialArrivalContext() {
    try {
      window.sessionStorage.removeItem(SOCIAL_ARRIVAL_STORAGE_KEY);
    } catch (err) {
      /* sessionStorage may be unavailable. */
    }
  }

  function trackCanonicalArrival() {
    if (canonicalArrivalSent || !analyticsProcessingAllowedNow()) return;

    var context = readSocialArrivalContext();
    if (!context || window.location.pathname !== context.destination_path) return;
    if (window.location.hash !== '#' + context.target_section) return;

    var anchor = document.getElementById(context.target_section);
    if (!anchor || anchor.id !== context.target_section) return;

    var pageContext = readKikiContext();
    if (pageContext.content_id !== context.destination_content_id) return;

    var detail = Object.assign({}, context, {
      event_category: 'canonical_arrival'
    });
    delete detail.captured_at;
    delete detail.destination_path;

    canonicalArrivalSent = dispatchSocialLandingEvent(SOCIAL_ARRIVAL_EVENT, detail);
    if (canonicalArrivalSent) clearSocialArrivalContext();
  }

  function onCtaClick(event) {
    if (!analyticsProcessingAllowedNow()) return;

    var cta = event.target.closest('[data-kiki-cta="true"]');
    if (!cta) return;

    var root = cta.closest('[data-kiki-component="social-landing"]');
    var role = cta.getAttribute('data-kiki-cta-role') || '';
    var detail = Object.assign({
      cta_label: cta.getAttribute('data-kiki-cta-label') || (cta.textContent || '').trim(),
      cta_href: cta.getAttribute('href') || '',
      cta_role: role,
      event_category: root ? 'social_landing' : 'cta'
    }, readSocialLandingContext(root, cta));

    var customEvent = cta.getAttribute('data-kiki-event') || '';
    if (customEvent) {
      if (root) {
        dispatchSocialLandingEvent(customEvent, detail);
      } else {
        dispatchWagn(customEvent, detail);
      }
    }

    /* Social landing CTAs are classified by data-kiki-cta-role:
         - "primary"   emits the configured landing CTA event
                       (default wagn:social_landing_cta_click).
         - "secondary" emits wagn:social_landing_secondary_cta_click only.
                       A secondary click must not be counted as a primary
                       conversion, so the configured landing CTA event is
                       NOT emitted for the secondary role.
         - "problem"   is the featured-problem guide link. The dedicated
                       problem handler (onProblemCtaClick) already emits
                       wagn:social_landing_problem_click, so this handler
                       must not double-count it as a primary CTA.
       CTAs that sit outside a social landing (root === null) fall back
       to the documented generic wagn:cta_click only. */
    var socialPrimarySent = false;
    if (root) {
      if (role === 'primary') {
        var ctaEvent = root.getAttribute('data-kiki-cta-event') || 'wagn:social_landing_cta_click';
        socialPrimarySent = dispatchSocialLandingEvent(ctaEvent, detail);
        if (ctaEvent !== 'wagn:social_landing_cta_click') {
          dispatchSocialLandingEvent('wagn:social_landing_cta_click', detail);
        }
      } else if (role === 'secondary') {
        dispatchSocialLandingEvent('wagn:social_landing_secondary_cta_click', detail);
      }
      /* role === 'problem' (and any future role) intentionally falls
         through without emitting a landing CTA event here. The generic
         wagn:cta_click below still fires so the broader funnel signal
         is preserved. */
    }

    if (socialPrimarySent) writeSocialArrivalContext(detail);
    if (root) {
      dispatchSocialLandingEvent('wagn:cta_click', detail);
    } else {
      dispatchWagn('wagn:cta_click', detail);
    }
  }

  function onProblemCardClick(event) {
    if (!analyticsProcessingAllowedNow()) return;

    var card = event.target.closest('[data-kiki-problem-link="true"]');
    if (!card) return;
    var item = card.closest('[data-kiki-problem-slug]');
    if (!item) return;

    dispatchWagn('wagn:problem_card_click', {
      problem_slug: item.getAttribute('data-kiki-problem-slug') || '',
      problem_id: item.getAttribute('data-kiki-problem-id') || '',
      cta_label: (card.textContent || '').trim(),
      cta_href: card.getAttribute('href') || '',
      event_category: 'problem_discovery'
    });

    var customEvent = card.getAttribute('data-kiki-event') || '';
    if (customEvent) {
      dispatchWagn(customEvent, {
        problem_slug: item.getAttribute('data-kiki-problem-slug') || '',
        problem_id: item.getAttribute('data-kiki-problem-id') || '',
        cta_href: card.getAttribute('href') || '',
        event_category: 'problem_discovery'
      });
    }
  }

  function onGuideAccordionClick(event) {
    if (!analyticsProcessingAllowedNow()) return;
    var summary = event.target.closest('.kiki-guide-modules__summary');
    if (!summary) return;
    var details = summary.closest('details[data-kiki-guide-event]');
    if (!details || details.open) return;
    var customEvent = details.getAttribute('data-kiki-guide-event') || '';
    if (!customEvent) return;
    dispatchWagn(customEvent, {
      guide_item: details.getAttribute('data-kiki-guide-item') || '',
      event_category: 'walk_guide'
    });
  }

  function dispatchGuideChecklistEvent(checklist, action) {
    if (!analyticsProcessingAllowedNow() || !checklist) return;
    var customEvent = checklist.getAttribute('data-kiki-checklist-event') || '';
    if (!customEvent) return;
    var items = Array.prototype.slice.call(
      checklist.querySelectorAll('[data-kiki-checklist-item]')
    );
    dispatchWagn(customEvent, {
      action: action,
      completed_count: items.filter(function (item) { return item.checked; }).length,
      total_count: items.length,
      event_category: 'guide_checklist'
    });
  }

  function onGuideChecklistChange(event) {
    var item = event.target.closest('[data-kiki-checklist-item]');
    if (!item) return;
    dispatchGuideChecklistEvent(item.closest('[data-kiki-checklist]'), 'update');
  }

  function onGuideChecklistClear(event) {
    var clear = event.target.closest('[data-kiki-checklist-clear]');
    if (!clear) return;
    var checklist = clear.closest('[data-kiki-checklist]');
    if (!checklist || !analyticsProcessingAllowedNow()) return;
    var customEvent = checklist.getAttribute('data-kiki-checklist-event') || '';
    if (!customEvent) return;
    dispatchWagn(customEvent, {
      action: 'clear',
      completed_count: 0,
      total_count: checklist.querySelectorAll('[data-kiki-checklist-item]').length,
      event_category: 'guide_checklist'
    });
  }

  function onProblemCtaClick(event) {
    if (!analyticsProcessingAllowedNow()) return;

    var link = event.target.closest('[data-kiki-problem-cta="true"]');
    if (!link) return;
    var root = link.closest('[data-kiki-component="social-landing"]');

    dispatchSocialLandingEvent('wagn:social_landing_problem_click', Object.assign({
      cta_label: (link.textContent || '').trim(),
      cta_href: link.getAttribute('href') || '',
      event_category: 'social_landing_problem'
    }, readSocialLandingContext(root, link)));
  }

  function onConsentChange() {
    refreshAttribution();
    trackViewProblemCards();
    trackSocialLandingView();
    trackCanonicalArrival();
    trackBreedHubView();
    /* Product bridge impressions are observer-driven; on consent change
       we only re-evaluate the bridges whose observers are still
       attached. Already-dispatched bridges are skipped by the once-only
       gate, and off-screen bridges stay observed for their next entry. */
    reevaluateVisibleProductBridges();
  }

  function bind() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', bind, { once: true });
      return;
    }

    refreshAttribution();
    trackViewProblemCards();
    trackSocialLandingView();
    trackCanonicalArrival();
    trackBreedHubView();
    /* Attach IntersectionObservers. This MUST NOT dispatch any
       wagn:product_bridge_view events at initialization; impressions
       are deferred to first viewport entry AND analytics grant. */
    onProductBridgeView();
    document.addEventListener('click', onCtaClick, false);
    document.addEventListener('click', onProblemCardClick, false);
    document.addEventListener('click', onProblemCtaClick, false);
    document.addEventListener('click', onGuideAccordionClick, false);
    document.addEventListener('change', onGuideChecklistChange, false);
    document.addEventListener('click', onGuideChecklistClear, false);
    document.addEventListener('click', onBreedEventClick, false);
    document.addEventListener('visitorConsentCollected', onConsentChange, false);
    document.addEventListener('kiki:privacy-api-ready', onConsentChange, false);
  }

  bind();
})();
